import React, { useState, useEffect, useCallback, useMemo } from "react";

const t = () => new Proxy({}, { get: (_, p) => (window as any).__ENGPORTAL_THEME?.[p] }) as any;

// ─── Types ──────────────────────────────────────────────────────────────
interface MaterialRow {
  [key: string]: any;
}

interface LocationSummary {
  location: string;
  count: number;
  totalQty: number;
  parts: string[];
}

// ─── Field mapping config ───────────────────────────────────────────────
// These map Epicor BAQ column names to our display fields.
// Update these once the actual BAQ columns are known.
const FIELD_MAP = {
  location: ['WarehouseCode', 'WhseCode', 'Warehouse', 'Location', 'BinNum', 'ResourceID', 'OpCode'],
  partNum: ['PartNum', 'Part_PartNum', 'PartNumber'],
  partDesc: ['PartDescription', 'Part_PartDescription', 'Description', 'PartDesc'],
  qty: ['OnhandQty', 'Quantity', 'QtyOnHand', 'Qty', 'QtyAvail'],
  lotNum: ['LotNum', 'Lot', 'LotNumber'],
  jobNum: ['JobNum', 'Job', 'JobNumber'],
  uom: ['UOM', 'IUM', 'UnitOfMeasure'],
};

function findField(row: any, candidates: string[]): string | null {
  for (const c of candidates) {
    if (row[c] !== undefined) return c;
  }
  return null;
}

function getVal(row: any, candidates: string[]): any {
  const field = findField(row, candidates);
  return field ? row[field] : null;
}

export default function MaterialTrackerPage() {
  const theme = t();
  const [data, setData] = useState<MaterialRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState<boolean | null>(null);

  // Filters
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [partSearch, setPartSearch] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Check connection
  useEffect(() => {
    fetch('/api/epicor/status/', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(d => setConnected(d.status === 'connected'))
      .catch(() => setConnected(false));
  }, []);

  // Fetch material location data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (partSearch) params.set('PartNum', partSearch);
      const qs = params.toString();
      const url = `/api/epicor/reports/material-location/${qs ? '?' + qs : ''}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (res.ok) {
        const result = await res.json();
        setData(result.data || []);
        setLastRefresh(new Date());
      } else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setError(err.detail || 'Failed to load material data');
      }
    } catch (e: any) {
      setError(e.message || 'Request failed');
    }
    setLoading(false);
  }, [partSearch]);

  // Auto-load on mount if connected
  useEffect(() => {
    if (connected) loadData();
  }, [connected]); // eslint-disable-line

  // ─── Derived Data ─────────────────────────────────────────────────────

  // Detect which fields are present in the data
  const detectedFields = useMemo(() => {
    if (!data.length) return { location: null, partNum: null, partDesc: null, qty: null, lotNum: null, jobNum: null, uom: null };
    const sample = data[0];
    return {
      location: findField(sample, FIELD_MAP.location),
      partNum: findField(sample, FIELD_MAP.partNum),
      partDesc: findField(sample, FIELD_MAP.partDesc),
      qty: findField(sample, FIELD_MAP.qty),
      lotNum: findField(sample, FIELD_MAP.lotNum),
      jobNum: findField(sample, FIELD_MAP.jobNum),
      uom: findField(sample, FIELD_MAP.uom),
    };
  }, [data]);

  // Get unique locations
  const locations = useMemo(() => {
    if (!detectedFields.location) return [];
    const locs = new Set<string>();
    data.forEach(row => {
      const loc = row[detectedFields.location!];
      if (loc) locs.add(String(loc));
    });
    return Array.from(locs).sort();
  }, [data, detectedFields]);

  // Location summaries for cards
  const locationSummaries = useMemo((): LocationSummary[] => {
    if (!detectedFields.location) return [];
    const map: Record<string, LocationSummary> = {};
    data.forEach(row => {
      const loc = String(row[detectedFields.location!] || 'Unknown');
      if (!map[loc]) map[loc] = { location: loc, count: 0, totalQty: 0, parts: [] };
      map[loc].count++;
      const qty = parseFloat(row[detectedFields.qty!]) || 0;
      map[loc].totalQty += qty;
      const pn = row[detectedFields.partNum!];
      if (pn && !map[loc].parts.includes(String(pn))) map[loc].parts.push(String(pn));
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [data, detectedFields]);

  // Filtered rows
  const filteredData = useMemo(() => {
    let rows = data;
    if (selectedLocation !== 'all' && detectedFields.location) {
      rows = rows.filter(r => String(r[detectedFields.location!]) === selectedLocation);
    }
    if (partSearch && detectedFields.partNum) {
      const q = partSearch.toLowerCase();
      rows = rows.filter(r => {
        const pn = String(r[detectedFields.partNum!] || '').toLowerCase();
        const desc = String(r[detectedFields.partDesc!] || '').toLowerCase();
        return pn.includes(q) || desc.includes(q);
      });
    }
    return rows;
  }, [data, selectedLocation, partSearch, detectedFields]);

  // All columns for fallback table
  const allColumns = data.length ? Object.keys(data[0]) : [];

  // ─── Styles ───────────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8,
    padding: '8px 12px', color: theme.text, fontSize: 13, outline: 'none',
  };

  const cardStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
    background: isSelected ? theme.accent + '15' : theme.surface,
    border: `1px solid ${isSelected ? theme.accent + '44' : theme.border}`,
    transition: 'all 0.15s', minWidth: 140, flex: '0 0 auto',
  });

  // ─── Location color mapping ───────────────────────────────────────────
  const locationColors = useMemo(() => {
    const palette = [
      theme.accent || '#6366f1',
      theme.green || '#22c55e',
      theme.yellow || '#eab308',
      theme.red || '#ef4444',
      '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#a855f7',
    ];
    const map: Record<string, string> = {};
    locations.forEach((loc, i) => {
      map[loc] = palette[i % palette.length];
    });
    return map;
  }, [locations, theme]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%', color: theme.text }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Material Tracker</h1>
          <p style={{ fontSize: 12, color: theme.textDim, margin: '4px 0 0' }}>
            Live material locations from Epicor — where is everything right now?
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && (
            <span style={{ fontSize: 10, color: theme.textDim }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={loadData}
            disabled={loading || !connected}
            style={{
              background: theme.accent, color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              opacity: loading || !connected ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {loading ? '↻ Refreshing...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Not connected banner */}
      {connected === false && (
        <div style={{
          padding: '14px 18px', borderRadius: 10,
          background: theme.yellow + '12', border: `1px solid ${theme.yellow}33`,
          fontSize: 13, color: theme.yellow,
        }}>
          Epicor is not connected. Add credentials to .env and restart the backend.
          Material tracking requires the <strong>zEP-MaterialLocation</strong> BAQ to be configured in Epicor.
        </div>
      )}

      {/* Filters Row */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center',
        background: theme.surface, padding: '12px 16px', borderRadius: 10,
        border: `1px solid ${theme.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.textDim, whiteSpace: 'nowrap' }}>LOCATION</label>
          <select
            value={selectedLocation}
            onChange={e => setSelectedLocation(e.target.value)}
            style={{ ...inputStyle, minWidth: 180, cursor: 'pointer' }}
          >
            <option value="all">All Locations ({data.length})</option>
            {locations.map(loc => {
              const summary = locationSummaries.find(s => s.location === loc);
              return (
                <option key={loc} value={loc}>
                  {loc} ({summary?.count || 0} items)
                </option>
              );
            })}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.textDim, whiteSpace: 'nowrap' }}>FIND PART</label>
          <input
            type="text"
            placeholder="Search part number..."
            value={partSearch}
            onChange={e => setPartSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && connected) loadData(); }}
            style={{ ...inputStyle, width: 200 }}
          />
        </div>
        <span style={{
          fontSize: 11, color: theme.textDim, padding: '4px 10px',
          background: theme.bg, borderRadius: 6, whiteSpace: 'nowrap',
        }}>
          {filteredData.length} item{filteredData.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Location Summary Cards */}
      {locationSummaries.length > 0 && selectedLocation === 'all' && (
        <div style={{
          display: 'flex', gap: 10, overflowX: 'auto', padding: '2px 0',
        }}>
          {locationSummaries.map(loc => {
            const color = locationColors[loc.location] || theme.accent;
            return (
              <div
                key={loc.location}
                onClick={() => setSelectedLocation(loc.location)}
                style={cardStyle(selectedLocation === loc.location)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = color + '66'; }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = selectedLocation === loc.location ? theme.accent + '44' : theme.border;
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: color,
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{loc.location}</span>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: theme.text, lineHeight: 1 }}>
                      {loc.count}
                    </div>
                    <div style={{ fontSize: 9, color: theme.textDim, marginTop: 2 }}>ITEMS</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: theme.text, lineHeight: 1 }}>
                      {loc.parts.length}
                    </div>
                    <div style={{ fontSize: 9, color: theme.textDim, marginTop: 2 }}>UNIQUE PARTS</div>
                  </div>
                </div>
                {loc.totalQty > 0 && (
                  <div style={{ fontSize: 10, color: theme.textDim, marginTop: 6 }}>
                    Total qty: {loc.totalQty.toLocaleString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Selected location header */}
      {selectedLocation !== 'all' && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', borderRadius: 10,
          background: (locationColors[selectedLocation] || theme.accent) + '10',
          border: `1px solid ${(locationColors[selectedLocation] || theme.accent)}33`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: locationColors[selectedLocation] || theme.accent,
            }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>{selectedLocation}</span>
            <span style={{ fontSize: 12, color: theme.textDim }}>
              — {filteredData.length} item{filteredData.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={() => setSelectedLocation('all')}
            style={{
              background: 'transparent', border: `1px solid ${theme.border}`,
              color: theme.textMuted, borderRadius: 6, padding: '4px 12px',
              fontSize: 11, cursor: 'pointer',
            }}
          >
            ← All Locations
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 16px', borderRadius: 8,
          background: theme.red + '12', border: `1px solid ${theme.red}33`,
          color: theme.red, fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Data Table */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background: theme.surface, borderRadius: 12,
        border: `1px solid ${theme.border}`, overflow: 'hidden', minHeight: 0,
      }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && !data.length ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textDim }}>
              Loading material data from Epicor...
            </div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textDim }}>
              {data.length === 0 && connected
                ? 'No material data returned. Ensure the zEP-MaterialLocation BAQ is configured in Epicor.'
                : data.length === 0
                ? 'Connect to Epicor to see material locations.'
                : 'No materials match the current filters.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
                  {/* Smart columns: show detected fields first, then any extras */}
                  {detectedFields.location && (
                    <th style={thStyle(theme)}>Location</th>
                  )}
                  {detectedFields.partNum && (
                    <th style={thStyle(theme)}>Part Number</th>
                  )}
                  {detectedFields.partDesc && (
                    <th style={thStyle(theme)}>Description</th>
                  )}
                  {detectedFields.qty && (
                    <th style={{ ...thStyle(theme), textAlign: 'right' }}>Qty</th>
                  )}
                  {detectedFields.uom && (
                    <th style={thStyle(theme)}>UOM</th>
                  )}
                  {detectedFields.lotNum && (
                    <th style={thStyle(theme)}>Lot</th>
                  )}
                  {detectedFields.jobNum && (
                    <th style={thStyle(theme)}>Job</th>
                  )}
                  {/* Extra columns not in our known mapping */}
                  {allColumns.filter(c =>
                    !Object.values(detectedFields).includes(c) && c !== 'RowMod' && c !== 'SysRowID'
                  ).map(col => (
                    <th key={col} style={thStyle(theme)}>{col.replace(/_/g, ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, i) => {
                  const loc = detectedFields.location ? String(row[detectedFields.location] || '') : '';
                  const color = locationColors[loc] || theme.textMuted;
                  return (
                    <tr key={i}
                        style={{ borderBottom: `1px solid ${theme.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {detectedFields.location && (
                        <td style={tdStyle()}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <span style={{ fontWeight: 600 }}>{loc}</span>
                          </div>
                        </td>
                      )}
                      {detectedFields.partNum && (
                        <td style={{ ...tdStyle(), fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
                          {row[detectedFields.partNum]}
                        </td>
                      )}
                      {detectedFields.partDesc && (
                        <td style={{ ...tdStyle(), maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row[detectedFields.partDesc] || '—'}
                        </td>
                      )}
                      {detectedFields.qty && (
                        <td style={{ ...tdStyle(), textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                          {parseFloat(row[detectedFields.qty]) ? Number(row[detectedFields.qty]).toLocaleString() : row[detectedFields.qty]}
                        </td>
                      )}
                      {detectedFields.uom && (
                        <td style={{ ...tdStyle(), color: theme.textDim }}>{row[detectedFields.uom] || ''}</td>
                      )}
                      {detectedFields.lotNum && (
                        <td style={{ ...tdStyle(), fontFamily: 'monospace', fontSize: 11 }}>{row[detectedFields.lotNum] || '—'}</td>
                      )}
                      {detectedFields.jobNum && (
                        <td style={{ ...tdStyle(), fontFamily: 'monospace', fontSize: 11 }}>{row[detectedFields.jobNum] || '—'}</td>
                      )}
                      {allColumns.filter(c =>
                        !Object.values(detectedFields).includes(c) && c !== 'RowMod' && c !== 'SysRowID'
                      ).map(col => (
                        <td key={col} style={tdStyle()}>
                          {row[col] != null ? String(row[col]) : '—'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Table footer with export */}
        {filteredData.length > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 16px', borderTop: `1px solid ${theme.border}`,
            background: theme.bg,
          }}>
            <span style={{ fontSize: 11, color: theme.textDim }}>
              Showing {filteredData.length} of {data.length} total records
            </span>
            <button
              onClick={() => {
                if (!filteredData.length) return;
                const headers = Object.keys(filteredData[0]);
                const csv = [
                  headers.join(','),
                  ...filteredData.map(row => headers.map(h => {
                    const val = String(row[h] ?? '').replace(/"/g, '""');
                    return val.includes(',') || val.includes('"') ? `"${val}"` : val;
                  }).join(','))
                ].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `material-location-${selectedLocation === 'all' ? 'all' : selectedLocation}-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
              }}
              style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: theme.green + '15', color: theme.green,
                border: `1px solid ${theme.green}33`, cursor: 'pointer',
              }}
            >
              Export CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Table Style Helpers ────────────────────────────────────────────────
function thStyle(theme: any): React.CSSProperties {
  return {
    padding: '8px 12px', textAlign: 'left', fontWeight: 600,
    color: theme.textDim, fontSize: 10, textTransform: 'uppercase',
    letterSpacing: 0.5, whiteSpace: 'nowrap',
    position: 'sticky', top: 0, background: theme.surface,
  };
}

function tdStyle(): React.CSSProperties {
  return {
    padding: '8px 12px', whiteSpace: 'nowrap',
  };
}
